import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Paper,
  Grid,
  TableContainer,
  Skeleton,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useEffect, useState } from "react";
import { UrlApi } from "../../../../../../../services/apirest";

const SaldosFacturasModal = ({ open, onClose, rowInfo }) => {
  const [data, setData] = useState({
    imputaciones: [],
    totalImputado: 0,
    factura: null,
  });
  const [loading, setLoading] = useState(true);

  const searchFactura = async (id) => {
    const response = await fetch(`${UrlApi}/comprobantes/saldoFacturas/${id}`);
    const result = await response.json();
    setData(result);
    setLoading(false);
  };

  const handleClose = () => {
    setData({ imputaciones: [], totalImputado: 0, factura: null });
    setLoading(true);
    onClose();
  };

  useEffect(() => {
    if (rowInfo?.id && open) {
      setLoading(true);
      searchFactura(rowInfo.id);
    }
  }, [rowInfo, open]);

  const factura = data?.factura;
  const imputaciones = data?.imputaciones || [];
  const totalImputado = Number(data?.totalImputado || 0);
  const totalFactura = Number(factura?.importe_total || 0);
  const saldoPendiente =
    isNaN(totalFactura) || isNaN(totalImputado)
      ? 0
      : totalFactura - totalImputado;

  const estado = !factura
    ? { label: "Sin datos", color: "default" }
    : saldoPendiente === 0
      ? { label: "Pagada", color: "success" }
      : totalImputado > 0
        ? { label: "Parcial", color: "warning" }
        : { label: "Pendiente", color: "error" };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ bgcolor: "primary.main", py: 2 }}>
          <Skeleton variant="text" width={200} height={30} />
        </DialogTitle>
        <DialogContent>
          <Box mb={3}>
            <Skeleton width="40%" height={20} />
            <Skeleton width="30%" height={20} />
          </Box>
          <Grid container spacing={2} mb={3}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={4} key={i}>
                <Paper sx={{ p: 2 }}>
                  <Skeleton width="60%" height={20} />
                  <Skeleton width="80%" height={30} />
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rectangular" height={200} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ bgcolor: "primary.main", color: "white", py: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <ReceiptLongIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">
                FACTURA
              </Typography>
              <Typography variant="subtitle2">
                {factura?.nro_comprobante}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={estado.label}
            sx={{
              bgcolor: "white",
              color: `${estado.color}.main`,
              fontWeight: "bold",
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Paper sx={{ p: 3, bgcolor: "grey.50" }} elevation={0}>
          <Box display="flex" gap={4} sx={{ justifyContent: "center" }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                CLIENTE
              </Typography>
              <Typography fontWeight="600">
                {factura?.razon_social?.trim()}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box>
              <Typography variant="caption" color="text.secondary">
                CUIT
              </Typography>
              <Typography fontWeight="600">{factura?.cuit_dni}</Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box>
              <Typography variant="caption" color="text.secondary">
                FECHA EMISIÓN
              </Typography>
              <Typography fontWeight="600">{factura?.fecha_emision}</Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box>
              <Typography variant="caption" color="text.secondary">
                CONDICIÓN IVA
              </Typography>
              <Typography fontWeight="600">{factura?.condicion_iva}</Typography>
            </Box>

            {rowInfo?.nro_remito && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    N° REMITO
                  </Typography>
                  <Typography fontWeight="600">{rowInfo.nro_remito}</Typography>
                </Box>
              </>
            )}
          </Box>
        </Paper>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {[
              { label: "Importe", value: totalFactura },
              {
                label: "Imputado",
                value: totalImputado,
                color: "success.main",
              },
              {
                label: "Saldo",
                value: saldoPendiente,
                color: saldoPendiente === 0 ? "success.main" : "error.main",
              },
            ].map((item, i) => (
              <Grid item xs={4} key={i}>
                <Paper
                  sx={{ p: 2, textAlign: "center", borderRadius: 2 }}
                  variant="outlined"
                >
                  <Typography variant="caption" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: item.color || "text.primary" }}
                  >
                    ${(item.value ?? 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ mb: 2, color: "info.main" }}
          >
            IMPUTACIONES
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Recibo</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell align="right">Importe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {imputaciones.length > 0 ? (
                  imputaciones.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography fontWeight="600">
                          {item.nro_recibo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.created_at
                          ? new Date(item.created_at).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        ${Number(item.importe).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Sin imputaciones
                    </TableCell>
                  </TableRow>
                )}
                {imputaciones.length > 0 && (
                  <TableRow sx={{ bgcolor: "grey.100" }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                      TOTAL IMPUTADO
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      ${totalImputado.toLocaleString()}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SaldosFacturasModal;
